import React, { useEffect, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import CoachAthleteDetailPage from "../components/scouting/CoachAthleteDetailPage";
import { useCoachAthlete } from "../hooks/useCoachAthlete";
import { useAuth } from "../context/AuthContext";
import { useLatestContactRequest } from "../hooks/queries/useContactRequests";
import { scoutThreadPath } from "../utils/scoutContactAction";

const CoachAthleteDetailRoute = () => {
  const navigate = useNavigate();
  const { athleteId } = useParams();
  const { scoutData } = useAuth();
  const { athlete, isLoading } = useCoachAthlete(athleteId);

  // Defense in depth (Ley 1581): the full profile (email/phone) is only
  // reachable once the athlete approved the contact request. The scout's
  // "Ver perfil completo" button already gates this in the UI; here we block a
  // programmatic/deep-link bypass. If no request exists (null) we allow, so we
  // don't break legitimate flows that never created one.
  const latestRequestQuery = useLatestContactRequest(athleteId, {
    enabled: Boolean(scoutData?.id),
  });

  const gateChecked =
    !scoutData?.id ||
    !athleteId ||
    latestRequestQuery.isSuccess ||
    latestRequestQuery.isError;
  const latest = latestRequestQuery.data ?? null;
  // Latched: the gate guards how the scout *arrives*. Sending a request from
  // this page turns `latest` into a pending row, and that must not eject them.
  // Keyed by athlete so moving to another profile re-runs the gate.
  const admittedRef = useRef(null);
  if (gateChecked && (!latest || latest.status === "approved")) {
    admittedRef.current = athleteId;
  }
  const allowed = admittedRef.current === athleteId;

  useEffect(() => {
    if (gateChecked && !allowed) {
      console.warn(
        "Acceso al perfil completo bloqueado: la solicitud de contacto no está aprobada.",
      );
    }
  }, [gateChecked, allowed]);

  if (!gateChecked || (isLoading && !athlete)) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-blue-900" size={32} />
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/scout/athletes" replace />;
  }

  return (
    <CoachAthleteDetailPage
      athlete={athlete}
      contactRequest={latest}
      onOpenThread={(request) => navigate(scoutThreadPath(request))}
      onBack={() => navigate("/scout/dashboard")}
    />
  );
};

export default CoachAthleteDetailRoute;
